/// <amd-module name="Build/Run/RunDetailsService" />
import { Approval as ContractApproval, ApprovalUpdateParameters } from "Build/Client/Contracts/Approval";
import {
    Build,
    BuildReason,
    BuildStatus,
    DefinitionReference,
    NewRetentionLease,
    RetentionLease,
    StageUpdateType,
    UpdateStageParameters,
    UpdateTagParameters
} from "Build/Client/Contracts/Build";
import { ArtifactsData, ChangesData, RetentionLeaseData, RunDetailsData } from "Build/Client/Contracts/DataProvider";
import { ResourcePipelinePermissions } from "Build/Client/Contracts/PipelinePermissions";
import { getApprovalClient } from "Build/Client/RestClient/Approval";
import { IBuildRestClient } from "Build/Client/RestClient/Build";
import { getPipelinePermissionsClient } from "Build/Client/RestClient/PipelinePermissions";
import { getPipelinesChecksClient } from "Build/Client/RestClient/PipelinesChecks";
import { CheckSuiteUpdateParameter } from "Build/Client/Contracts/PipelinesChecks";
import { handleError } from "Build/Common/Library/ErrorHandler";
import { Security } from "Build/Common/Library/Security";
import { PermissionsStore } from "Build/Common/Library/Stores/Permissions";
import { IDictionaryStringTo, IRunDetailsPermissions, IRunDetailsService } from "Build/Run/RunDetailsService.types";
import { CodeCoverageSummary, IRunExternalService, TestResults } from "Build/Run/RunExternalService";
import { ISignalRService } from "Build/Run/SignalRService";
import { RepositoryTypes } from "Build/SourceProviders/Common";
import { ITfsPageService } from "Tfs/Platform/Page";
import { IObservableArray, IReadonlyObservableValue, ObservableArray, ObservableValue } from "VSS/Core/Observable";
import { TimerManagement } from "VSS/Core/TimerManagement";
import * as StringUtil from "VSS/Core/Util/String";
import { IVssPageContext, Services, VssService } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { IVssPageService } from "VSS/Platform/Page";
import { IVssTelemetryService } from "VSS/Platform/Telemetry";
import { duration } from "VSSUI/Utilities/Date";

const c_batchingDelayInMs = 3 * 1000;
const c_infiniteLeaseDays = 365 * 1000;
const c_noLiveDataInMs = 90 * 1000;
const c_updateDataDelayInMs = 700;
const c_EnableTraceabilityForRunViewFeatureFlag = "Pipelines.Traceability.EnableTraceabilityForRunView";
const c_traceabilityCountRetryIntervalInMs = 2 * 1000;
const c_traceabilityCountMaxRetryAttempts = 2;
const c_userRetentionLeaseType = "User";

export class RunDetailsService extends VssService implements IRunDetailsService {
    private _runDetails: ObservableValue<RunDetailsData>;
    private _isDeleted: ObservableValue<boolean>;
    private _signalR: ISignalRService | undefined;
    private _projectId: string;
    private _security: Security;
    private _permissionsStore: PermissionsStore;
    private _client: IBuildRestClient;
    private _userId: string;
    private _contributionService: IVssContributionService;
    private _externalService: IRunExternalService;
    private _testResults: ObservableValue<TestResults>;
    private _codeCoverageResults: ObservableValue<CodeCoverageSummary>;
    private _associatedChanges: ObservableValue<ChangesData>;
    private _traceabilityChangesCount: ObservableValue<number>;
    private _traceabilityWorkItemsCount: ObservableValue<number>;
    private _fallbackTraceabilityFlag: ObservableValue<boolean>;
    private _artifacts: ObservableValue<ArtifactsData>;
    private _retentionLeases: ObservableArray<RetentionLeaseData>; // an Observable wrapper around _runDetails.retentionLeases
    private _timerManagement: TimerManagement;
    private _telemetryService: IVssTelemetryService;
    private _tempTestResults: TestResults;
    private _tempCodeCoverageResults: CodeCoverageSummary;
    private _tempAssociatedChanges: ChangesData;
    private _tempTraceabilityWorkItemsCount: number;
    private _tempTraceabilityChangesCount: number;
    private _tempArtifactsData: ArtifactsData;
    private _refreshDataDebounced: () => void;
    private _refreshArtifactsDataDebounced: () => void;
    private _isRunViewTraceabilityFeatureFlagEnabled: boolean;
    private _liveCheckTimer: number | undefined;

    public get data(): IReadonlyObservableValue<RunDetailsData> {
        return this._runDetails;
    }

    public get associatedChanges(): IReadonlyObservableValue<ChangesData> {
        return this._associatedChanges;
    }

    public get fallbackTraceabilityFlag(): IReadonlyObservableValue<boolean> {
        return this._fallbackTraceabilityFlag;
    }

    public get traceabilityChangesCount(): IReadonlyObservableValue<number> {
        return this._traceabilityChangesCount;
    }

    public get traceabilityWorkItemsCount(): IReadonlyObservableValue<number> {
        return this._traceabilityWorkItemsCount;
    }

    public get artifacts(): IReadonlyObservableValue<ArtifactsData> {
        return this._artifacts;
    }

    public get isDeleted(): IReadonlyObservableValue<boolean> {
        return this._isDeleted;
    }

    public get testRunResults(): IReadonlyObservableValue<TestResults> {
        return this._testResults;
    }

    public get codeCoverageResults(): IReadonlyObservableValue<CodeCoverageSummary> {
        return this._codeCoverageResults;
    }

    public get retentionLeases(): IObservableArray<RetentionLeaseData> {
        return this._retentionLeases;
    }

    public refreshData(force: boolean = false) {
        if (this._liveCheckTimer) {
            this._timerManagement.clearInterval(this._liveCheckTimer);
            this._liveCheckTimer = this._timerManagement.setInterval(this._refreshData, c_noLiveDataInMs);
        }
        if (force) {
            this._refreshData();
        } else {
            this._refreshDataDebounced();
        }
    }

    public refreshArtifactsData() {
        this._refreshArtifactsDataDebounced();
    }

    public getPermissions(): IRunDetailsPermissions {
        const run = this._runDetails.value;
        let canRunBuild = false;
        let canRetainBuild = false;
        let canCancelBuild = false;
        let canDeleteBuild = false;
        let canEditPipeline = false;
        let canEditTags = false;

        if (this._runDetails) {
            canRunBuild = this._security.canQueueBuildById(run.pipeline.id.toString(), run.pipeline.path);
            canRetainBuild = this._security.canRetainBuildByDefinitionId(run.pipeline.id.toString(), run.pipeline.path);
            canCancelBuild = this._security.canCancelBuildByUserId(
                this._userId,
                run.pipeline.id.toString(),
                run.pipeline.path,
                run.requestedForId,
                run.requestedById
            );
            canDeleteBuild = this._security.canDeleteBuildByDefinitionId(run.pipeline.id.toString(), run.pipeline.path);
            canEditPipeline = this._security.canEditDefinition({ id: run.pipeline.id, path: run.pipeline.path } as DefinitionReference);
            canEditTags = this._security.canEditTagsByUserId(
                this._userId,
                run.pipeline.id.toString(),
                run.pipeline.path,
                run.requestedForId,
                run.requestedById
            );
        }

        return {
            canRunBuild: canRunBuild,
            canRetainBuild: canRetainBuild,
            canCancelBuild: canCancelBuild,
            canDeleteBuild: canDeleteBuild,
            canEditPipeline: canEditPipeline,
            canEditTags: canEditTags
        };
    }

    public getTags(): Promise<string[]> {
        return this._client.getTags(this._projectId);
    }

    public cancelRun(): void {
        const run = this._runDetails.value;

        this._client
            .updateBuild(
                {
                    status: BuildStatus.Cancelling
                } as Build,
                this._projectId,
                run.id
            )
            .then(build => {
                this.refreshData();
            })
            .catch(handleError);
    }

    public deleteRun(): void {
        const run = this._runDetails.value;

        this._client
            .deleteBuild(this._projectId, run.id)
            .then(() => {
                this._isDeleted.value = true;
                this.refreshData();
            })
            .catch(handleError);
    }

    public cancelStage(stageId: string): void {
        const updateStageParameters = { state: StageUpdateType.Cancel } as UpdateStageParameters;
        this._client
            .updateStage(updateStageParameters, this._runDetails.value.id, stageId, this._projectId)
            .then(() => {
                this.refreshData();
            })
            .catch(handleError);
    }

    public retryStage(stageId: string, forceRetryAllJobs: boolean = false, retryDependencies: boolean = true): void {
        const updateStageParameters = { state: StageUpdateType.Retry, forceRetryAllJobs: forceRetryAllJobs, retryDependencies: retryDependencies} as UpdateStageParameters;
        this._client
            .updateStage(updateStageParameters, this._runDetails.value.id, stageId, this._projectId)
            .then(() => {
                this.refreshData();
            })
            .catch(handleError);
    }

    public retryPipeline(): void {
        this._client
            .updateBuild({} as Build, this._projectId, this._runDetails.value.id, true)
            .then(() => {
                this.refreshData();
            })
            .catch(handleError);
    }

    public retainBuild(): void {
        if (this._userId) {
            const run = this._runDetails.value;
            const leases = this._retentionLeases.value.filter(lease => this._matchUserLease(lease));
            if (!leases || leases.length === 0) {
                const newLease: NewRetentionLease = {
                    daysValid: c_infiniteLeaseDays,
                    definitionId: run.pipeline.id,
                    ownerId: `${c_userRetentionLeaseType}:${this._userId}`,
                    protectPipeline: false,
                    runId: run.id
                };

                this._client.addRetentionLeases([newLease], this._projectId).then((addedLease: RetentionLease[]) => {
                    this._refreshData();
                }, handleError);
            }
        }
    }

    public stopRetainingBuild(): void {
        if (this._userId) {
            const leases = this._retentionLeases.value.filter(lease => this._matchUserLease(lease));
            if (leases && leases.length > 0) {
                const leaseIds = leases.map(value => value.id);
                this.deleteRetentionLeases(leaseIds);
            }
        }
    }

    public updateApprovals(approvals: ApprovalUpdateParameters[]): Promise<void | ContractApproval[]> {
        return getApprovalClient(this.pageContext).updateApprovals(approvals, this._projectId);
    }

    public updateCheckSuite(checkSuiteId: string, checkRunUpdateParameter: CheckSuiteUpdateParameter) {
        return getPipelinesChecksClient(this.pageContext)
            .updateCheckSuite(checkRunUpdateParameter, this._projectId, checkSuiteId);
    }

    public updatePermissions(
        permissions: ResourcePipelinePermissions,
        resourceType: string,
        resourceId: string
    ): Promise<void | ResourcePipelinePermissions> {
        return getPipelinePermissionsClient(this.pageContext)
            .updatePipelinePermisionsForResource(permissions, this._projectId, resourceType, resourceId)
            .catch(handleError);
    }

    public async updateTags(newTagSet: string[]): Promise<void> {
        const run = this._runDetails.value;
        const oldTagSet = run.tags;
        const addedTags = newTagSet.filter(tag => oldTagSet.indexOf(tag) < 0);
        const removedTags = oldTagSet.filter(tag => newTagSet.indexOf(tag) < 0);

        try {
            const updateTagParameters: UpdateTagParameters = { tagsToAdd: addedTags, tagsToRemove: removedTags };
            run.tags = await this._client.updateBuildTags(updateTagParameters, this._projectId, run.id);
        } catch (exception) {
            handleError(exception);
        }
        this._runDetails.value = run;
    }

    public startListening() {
        if (this._runDetails && this._runDetails.value) {
            this._signalR = this.pageContext.getService<ISignalRService>("ISignalRService");
            this._signalR.watch(this._runDetails.value.id);
            this._signalR.watchTestHub(this._runDetails.value.id, this._runDetails.value);
        }
    }

    public stopListening() {
        if (this._signalR) {
            this._signalR.endWatch(this._runDetails.value.id);
            this._signalR = undefined;
        }
    }

    public _serviceStart(pageContext: IVssPageContext): void {
        super._serviceStart(pageContext);
        this._initialize();
    }

    public _serviceRestart(pageContext: IVssPageContext): void {
        super._serviceRestart(pageContext);
        this._initialize();
    }

    public _serviceEnd(pageContext: IVssPageContext): any {
        super._serviceEnd(pageContext);
        this._timerManagement.clearAllTimers();
    }

    public async updateTestPassRate() {
        if (this._runDetails) {
            const results = await this._externalService.getTestResultsForRun(this._runDetails.value.id, this._runDetails.value.status);
            if (results) {
                this._tempTestResults = results;
                const dataReady = this._shouldUpdateData();
                if (dataReady) {
                    this._testResults.value = results;
                    if (dataReady === DataState.Ready) {
                        if (this.isRunSummaryTraceabilityEnabled && !this.fallbackTraceabilityFlag.value) {
                            this._traceabilityWorkItemsCount.value = this._tempTraceabilityWorkItemsCount;
                            this._traceabilityChangesCount.value = this._tempTraceabilityChangesCount;
                        } else {
                            this._associatedChanges.value = this._tempAssociatedChanges;
                        }
                        this._artifacts.value = this._tempArtifactsData;
                        this._codeCoverageResults.value = this._tempCodeCoverageResults;
                    }
                } else {
                    this._updateData();
                }
            }
        }
    }

    public async updateCodeCoverageSummary() {
        if (this._runDetails) {
            const results = await this._externalService.getCodeCoverageSummary(this._runDetails.value.id, this._runDetails.value.status);
            if (results) {
                this._tempCodeCoverageResults = results;
                const dataReady = this._shouldUpdateData();
                if (dataReady) {
                    this._codeCoverageResults.value = results;
                    if (dataReady === DataState.Ready) {
                        if (this.isRunSummaryTraceabilityEnabled && !this.fallbackTraceabilityFlag.value) {
                            this._traceabilityWorkItemsCount.value = this._tempTraceabilityWorkItemsCount;
                            this._traceabilityChangesCount.value = this._tempTraceabilityChangesCount;
                        } else {
                            this._associatedChanges.value = this._tempAssociatedChanges;
                        }
                        this._artifacts.value = this._tempArtifactsData;
                        this._testResults.value = this._tempTestResults;
                    }
                } else {
                    this._updateData();
                }
            }
        }
    }

    public get isRunSummaryTraceabilityEnabled(): boolean {
        // traceability will be enabled once the FF is enabled and
        // the run is not a PR run and repositoryTypes should be supported types
        return (
            this._isRunViewTraceabilityFeatureFlagEnabled &&
            this._runDetails &&
            this._runDetails.value &&
            this._runDetails.value.reason != BuildReason.PullRequest &&
            (StringUtil.equals(this._runDetails.value.sourceRepositoryType, RepositoryTypes.TfsGit, true) ||
                StringUtil.equals(this._runDetails.value.sourceRepositoryType, RepositoryTypes.GitHub, true))
        );
    }

    public deleteRetentionLeases(ids: number[]) {
        this._client
            .deleteRetentionLeasesById(this._projectId, ids)
            .then(() => {
                this.refreshData();
            })
            .catch(handleError);
    }

    private _shouldUpdateData(): DataState {
        if (this._tempArtifactsData && this._tempTestResults && this._tempTestResults.testsCount != -1) {
            //  If feature is not-enabled then check for associated count, otherwise check for traceability count
            if (this.isRunSummaryTraceabilityEnabled && !this.fallbackTraceabilityFlag.value) {
                if (this._tempTraceabilityWorkItemsCount && this._tempTraceabilityChangesCount) {
                    return DataState.Ready;
                }
            } else {
                if (this._tempAssociatedChanges) {
                    return DataState.Ready;
                }
            }
        }
        if (
            this._runDetails &&
            (this._runDetails.value.status === BuildStatus.InProgress ||
                this._runDetails.value.status === BuildStatus.NotStarted ||
                this._runDetails.value.status === BuildStatus.Postponed)
        ) {
            return DataState.Partial;
        }

        return DataState.NotReady;
    }

    // debounced to throttle how frequently we get updated data
    private _refreshData = () => {
        if (this._runDetails && this._runDetails.value) {
            const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
            contributionService
                .getDataAsync<RunDetailsData>("ms.vss-build-web.run-details-data-provider", { buildId: this._runDetails.value.id.toString() }, true)
                .then((data: RunDetailsData | undefined) => {
                    if (data) {
                        if (data.status === BuildStatus.Completed && this._liveCheckTimer) {
                            this._timerManagement.clearInterval(this._liveCheckTimer);
                            this._liveCheckTimer = undefined;
                        }

                        const previousData = this._runDetails.value;
                        this._runDetails.value = this._standardizeData(data);
                        this._retentionLeases.value = this._runDetails.value.retentionLeases;

                        // refresh the test results when the build completes
                        if (this._runDetails.value.status === BuildStatus.Completed) {
                            this.updateTestPassRate();
                            this.updateCodeCoverageSummary();
                            if (this._signalR) {
                                this._signalR.endWatchTestHub(this._runDetails.value.id);
                            }
                        }

                        // if updated build has source version and earlier version of build did not, try to calculate changes one more time
                        if (data.sourceVersion && (!previousData || !previousData.sourceVersion)) {
                            this._fetchChangeData();
                        }
                    }
                });
        }
    };

    private _updateData = () => {
        if (this.isRunSummaryTraceabilityEnabled && !this.fallbackTraceabilityFlag.value) {
            if (this._tempTraceabilityWorkItemsCount) {
                this._traceabilityWorkItemsCount.value = this._tempTraceabilityWorkItemsCount;
            }
            if (this._tempTraceabilityChangesCount) {
                this._traceabilityChangesCount.value = this._tempTraceabilityChangesCount;
            }
        } else {
            if (this._tempAssociatedChanges) {
                this._associatedChanges.value = this._tempAssociatedChanges;
            }
        }
        if (this._tempTestResults) {
            this._testResults.value = this._tempTestResults;
        }
        if (this._tempArtifactsData) {
            this._artifacts.value = this._tempArtifactsData;
        }

        if (this._codeCoverageResults) {
            this._codeCoverageResults.value = this._tempCodeCoverageResults;
        }
    };

    // debounced to throttle how frequently we get updated data
    private _refreshArtifactsData = () => {
        this._getArtifactsData();
    };

    private _publishTraceabilityProviderTelemetry(
        currentRunId: string,
        sourceType: string,
        timeTaken: string,
        retryCount: number,
        commitsCount?: number,
        workItemsCount?: number
    ) {
        const eventProperties: IDictionaryStringTo<any> = {};
        eventProperties["currentRunId"] = currentRunId;
        eventProperties["sourceType"] = sourceType;
        eventProperties["timeTaken"] = timeTaken;
        eventProperties["retryCount"] = retryCount;
        const area = "Traceability";
        let feature = "RunViewChangesCount";

        if (commitsCount != undefined) {
            eventProperties["commitsCount"] = commitsCount;
        } else {
            eventProperties["workItemsCount"] = workItemsCount;
            feature = "RunViewWorkItemsCount";
        }
        this._telemetryService.publishEvent(area, feature, eventProperties);
    }

    private _publishGetChangesTelemetry(sourceType: string, commitsCount: number, timeTaken: string) {
        const eventProperties: IDictionaryStringTo<any> = {};
        eventProperties["sourceType"] = sourceType;
        eventProperties["commitsCount"] = commitsCount;
        eventProperties["timeTaken"] = timeTaken;

        this._telemetryService.publishEvent("Pipelines", "GetChanges", eventProperties);
    }

    private _initialize() {
        const tfsPageService = this.pageContext.getService<ITfsPageService>("ITfsPageService");
        const tfsPageData = tfsPageService.getData();
        if (tfsPageData && tfsPageData.project) {
            this._projectId = tfsPageData.project.id;
        }

        const vssPageService = this.pageContext.getService<IVssPageService>("IVssPageService");
        const vssPageData = vssPageService.getData();
        if (vssPageData && vssPageData.user) {
            this._userId = vssPageData.user.id;
        }

        this._isRunViewTraceabilityFeatureFlagEnabled = isFeatureFlagEnabled(this.pageContext, c_EnableTraceabilityForRunViewFeatureFlag, false);

        this._externalService = this.pageContext.getService<IRunExternalService>("IRunExternalService");

        this._contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        const data = this._contributionService.getData<RunDetailsData>("ms.vss-build-web.run-details-data-provider");

        this._testResults = new ObservableValue<TestResults>({
            testsPassRate: -1,
            testsCount: -1
        });

        this._codeCoverageResults = new ObservableValue<CodeCoverageSummary>({
            coverage: "",
            coverageDetailedSummaryStatus:""
        });

        this._traceabilityChangesCount = new ObservableValue<number>(0);

        this._traceabilityWorkItemsCount = new ObservableValue<number>(0);

        this._fallbackTraceabilityFlag = new ObservableValue<boolean>(false);

        this._associatedChanges = new ObservableValue<ChangesData>({
            changes: [],
            workItems: [],
            previousSourceVersion: ""
        });

        this._artifacts = new ObservableValue<ArtifactsData>({
            publishedArtifactsCount: 0,
            sources: [],
            consumedArtifactsCount: 0
        });
        this._isDeleted = new ObservableValue<boolean>(false);

        if (data) {
            this._runDetails = new ObservableValue<RunDetailsData>(this._standardizeData(data));
            this._retentionLeases = new ObservableArray<RetentionLeaseData>(data.retentionLeases);
            this._permissionsStore = new PermissionsStore({ pageContext: this.pageContext });
            this._security = new Security({
                projectId: this._projectId,
                store: this._permissionsStore
            });

            this._fetchChangeData();
            this._getArtifactsData();
            this.updateTestPassRate();
            this.updateCodeCoverageSummary();
        }

        this._client = this.pageContext.getRestClient<IBuildRestClient>("IBuildRestClient");
        this._timerManagement = new TimerManagement();
        this._refreshDataDebounced = this._timerManagement.debounce(this._refreshData, c_batchingDelayInMs);
        this._refreshArtifactsDataDebounced = this._timerManagement.debounce(this._refreshArtifactsData, c_batchingDelayInMs);
        this._updateData = this._timerManagement.debounce(this._updateData, c_updateDataDelayInMs);
        this._telemetryService = this.pageContext.getService<IVssTelemetryService>("IVssTelemetryService");
        if (this._runDetails && this._runDetails.value && this._runDetails.value.status !== BuildStatus.Completed) {
            this._liveCheckTimer = this._timerManagement.setInterval(this._refreshData, c_noLiveDataInMs);
        }
    }

    private _fetchChangeData() {
        if (this._runDetails && this._runDetails.value) {
            if (this.isRunSummaryTraceabilityEnabled && !this.fallbackTraceabilityFlag.value) {
                this._getRunViewTraceabilityData();
            } else {
                this._getChangeData();
            }
        }
    }

    private _fetchTraceabilityChangesCount = (dataProviderParams: { currentRunId: string }, firstFetchStartTime: Date, retryCount: number = 0) => {
        this._contributionService
            .getDataAsync<any>("ms.vss-build-web.traceability-run-changes-count-data-provider", dataProviderParams, true)
            .then(data => {
                const changesCount: number = data && data.changesCount != undefined ? data.changesCount : -1;
                if (changesCount > -1) {
                    // This data provider return changes count.
                    this._tempTraceabilityChangesCount = changesCount;
                    const dataReady = this._shouldUpdateData();

                    if (dataReady) {
                        this._traceabilityChangesCount.value = changesCount;
                        if (dataReady === DataState.Ready) {
                            this._testResults.value = this._tempTestResults;
                            this._artifacts.value = this._tempArtifactsData;
                            this._codeCoverageResults.value = this._tempCodeCoverageResults;
                        }
                    } else {
                        this._updateData();
                    }
                }

                if (changesCount === -1) {
                    if (retryCount < c_traceabilityCountMaxRetryAttempts) {
                        setTimeout(
                            () => this._fetchTraceabilityChangesCount(dataProviderParams, firstFetchStartTime, retryCount + 1),
                            c_traceabilityCountRetryIntervalInMs
                        );
                    } else {
                        //Fallback to older API calls as data not returned by new API calls
                        this._fallbackTraceabilityFlag.value = true;
                        this._getChangeData();
                    }
                } else {
                    const timeTaken = duration(firstFetchStartTime, new Date());
                    //  Telemetry publish for changes count data provider
                    this._publishTraceabilityProviderTelemetry(
                        dataProviderParams.currentRunId,
                        this._runDetails.value.sourceRepositoryType,
                        timeTaken,
                        retryCount,
                        changesCount
                    );
                }
            });
    };

    private _fetchTraceabilityWorkItemsCount = (dataProviderParams: { currentRunId: string }, firstFetchStartTime: Date, retryCount: number = 0) => {
        this._contributionService
            .getDataAsync<any>("ms.vss-build-web.traceability-run-workitems-count-data-provider", dataProviderParams, true)
            .then(data => {
                const workItemsCount: number = data && data.workItemsCount != undefined ? data.workItemsCount : -1;
                if (workItemsCount > -1) {
                    // This data provider return workItems count.
                    this._tempTraceabilityWorkItemsCount = workItemsCount;
                    const dataReady = this._shouldUpdateData();

                    if (dataReady) {
                        this._traceabilityWorkItemsCount.value = workItemsCount;
                        if (dataReady === DataState.Ready) {
                            this._testResults.value = this._tempTestResults;
                            this._artifacts.value = this._tempArtifactsData;
                            this._codeCoverageResults.value = this._tempCodeCoverageResults;
                        }
                    } else {
                        this._updateData();
                    }
                }

                if (workItemsCount === -1) {
                    if (retryCount < c_traceabilityCountMaxRetryAttempts) {
                        setTimeout(
                            () => this._fetchTraceabilityWorkItemsCount(dataProviderParams, firstFetchStartTime, retryCount + 1),
                            c_traceabilityCountRetryIntervalInMs
                        );
                    } else {
                        //Fallback to older API calls as data not returned by new API calls
                        this._fallbackTraceabilityFlag.value = true;
                        this._getChangeData();
                    }
                } else {
                    const timeTaken = duration(firstFetchStartTime, new Date());
                    //  Telemetry publish for changes count data provider
                    this._publishTraceabilityProviderTelemetry(
                        dataProviderParams.currentRunId,
                        this._runDetails.value.sourceRepositoryType,
                        timeTaken,
                        retryCount,
                        undefined,
                        workItemsCount
                    );
                }
            });
    };

    private _getRunViewTraceabilityData() {
        var dataProviderParams = {
            currentRunId: this._runDetails.value.id.toString()
        };

        const start_TraceabilityChangesProvider = new Date();
        this._fetchTraceabilityChangesCount(dataProviderParams, start_TraceabilityChangesProvider);

        const start_TraceabilityWorkItemsProvider = new Date();
        this._fetchTraceabilityWorkItemsCount(dataProviderParams, start_TraceabilityWorkItemsProvider);
    }

    private _getChangeData() {
        const start = new Date();
        const params = {
            repositoryId: this._runDetails.value.sourceRepositoryId,
            repositoryType: this._runDetails.value.sourceRepositoryType,
            buildId: this._runDetails.value.id.toString(),
            serviceEndpointId: this._runDetails.value.sourceEndpointId,
            includeSourceChanges: false
        };

        this._contributionService.getDataAsync<ChangesData>("ms.vss-build-web.run-details-changes-data-provider", params, true).then(data => {
            if (data) {
                this._tempAssociatedChanges = data;
                const dataReady = this._shouldUpdateData();
                if (dataReady) {
                    this._associatedChanges.value = data;
                    if (dataReady === DataState.Ready) {
                        this._testResults.value = this._tempTestResults;
                        this._artifacts.value = this._tempArtifactsData;
                        this._codeCoverageResults.value = this._tempCodeCoverageResults;
                    }
                } else {
                    this._updateData();
                }
                const commitsCount = data && data.changes ? data.changes.length : 0;
                const timeTaken = duration(start, new Date());
                this._publishGetChangesTelemetry(this._runDetails.value.sourceRepositoryType, commitsCount, timeTaken);
            }
        });
    }

    private _getArtifactsData() {
        if (this._runDetails && this._runDetails.value) {
            const params = {
                buildId: this._runDetails.value.id.toString()
            };

            this._contributionService.getDataAsync<ArtifactsData>("ms.vss-build-web.run-details-artifacts-data-provider", params, true).then(data => {
                if (data) {
                    this._tempArtifactsData = data;
                    const dataReady = this._shouldUpdateData();
                    if (dataReady) {
                        this._artifacts.value = data;
                        if (dataReady === DataState.Ready) {
                            this._testResults.value = this._tempTestResults;
                            if (this.isRunSummaryTraceabilityEnabled && !this.fallbackTraceabilityFlag.value) {
                                this._traceabilityWorkItemsCount.value = this._tempTraceabilityWorkItemsCount;
                                this._traceabilityChangesCount.value = this._tempTraceabilityChangesCount;
                            } else {
                                this._associatedChanges.value = this._tempAssociatedChanges;
                            }
                            this._codeCoverageResults.value = this._tempCodeCoverageResults;
                        }
                    } else {
                        this._updateData();
                    }
                }
            });
        }
    }

    private _standardizeData(data: RunDetailsData): RunDetailsData {
        if (!data.jobs) {
            data.jobs = [];
        }
        if (!data.stages) {
            data.stages = [];
        }
        if (!data.tags) {
            data.tags = [];
        }
        if (!data.issues) {
            data.issues = [];
        }

        return data;
    }

    private _matchUserLease(leaseData: RetentionLeaseData) {
        return (
            leaseData &&
            leaseData.leaseType === c_userRetentionLeaseType &&
            leaseData.leaseOwnerIdentityRef &&
            leaseData.leaseOwnerIdentityRef.id === this._userId
        );
    }
}

const enum DataState {
    NotReady = 0,
    Ready = 1,
    Partial = 2
}

export function getRunDetailsService(pageContext: IVssPageContext): IRunDetailsService {
    return pageContext.getService<IRunDetailsService>("IRunDetailsService");
}

Services.add("IRunDetailsService", { serviceFactory: RunDetailsService });
